"use client";

import useSWR from "swr";
import type { ServerStatus } from "@/lib/types";

const fetcher = (url: string) => fetch(url).then((r) => r.json() as Promise<ServerStatus>);

export default function LiveStatus({
  slug,
  initial,
}: {
  slug: string;
  initial: ServerStatus;
}) {
  const { data } = useSWR<ServerStatus>(`/api/status/${slug}`, fetcher, {
    refreshInterval: 10_000,
    fallbackData: initial,
    revalidateOnFocus: true,
  });

  const s = data ?? initial;

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
      <div className="h-0.5 bg-[var(--surface-2)] overflow-hidden">
        <div
          key={s.fetchedAt}
          className="refresh-bar h-full bg-[var(--accent)]"
          style={{ width: "100%" }}
        />
      </div>
      <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)] bg-[var(--surface-2)]">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">
          실시간 상태
        </h2>
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
            s.online
              ? "bg-[var(--accent-soft)] text-[var(--accent-strong)]"
              : "bg-[var(--background)] text-[var(--muted)] border border-[var(--border)]"
          }`}
        >
          {s.online ? (
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-[var(--accent)] opacity-60 animate-ping" />
              <span className="relative inline-block w-2 h-2 rounded-full bg-[var(--accent)]" />
            </span>
          ) : (
            <span className="w-2 h-2 rounded-full bg-[var(--muted)]" />
          )}
          {s.online ? "Online" : "Offline"}
        </span>
      </div>

      <div className="p-5">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold tabular-nums leading-none">
            {s.online && s.players ? s.players.online : "—"}
          </span>
          {s.online && s.players && (
            <span className="text-base text-[var(--muted)] tabular-nums">
              / {s.players.max}
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-[var(--muted)]">현재 접속자</div>

        <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm pt-4 border-t border-[var(--border)]">
          <div>
            <dt className="text-xs text-[var(--muted)]">버전</dt>
            <dd className="mt-0.5 mono">{s.version ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--muted)]">핑</dt>
            <dd className="mt-0.5 tabular-nums">
              {s.latencyMs !== undefined ? `${s.latencyMs}ms` : "—"}
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="text-xs text-[var(--muted)]">마지막 갱신</dt>
            <dd className="mt-0.5 mono text-xs">
              {new Date(s.fetchedAt).toLocaleTimeString("ko-KR")}
            </dd>
          </div>
        </dl>

      {s.motd && (
        <div className="mt-4">
          <div className="text-[var(--muted)] text-sm">MOTD</div>
          <pre className="mt-1 whitespace-pre-wrap break-words text-sm bg-[var(--surface-2)] border border-[var(--border)] rounded-lg p-3 mono">
            {s.motd}
          </pre>
        </div>
      )}

      {s.online && s.players?.sample && s.players.sample.length > 0 && (
        <div className="mt-4">
          <div className="text-[var(--muted)] text-sm">접속 중인 플레이어</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {s.players.sample.map((name) => (
              <span
                key={name}
                className="text-xs px-2 py-1 rounded-md bg-[var(--surface-2)] border border-[var(--border)] mono text-[var(--foreground)]"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {!s.online && s.error && (
        <div className="mt-4 text-sm text-[var(--muted)]">
          서버에 연결할 수 없습니다.
        </div>
      )}
      </div>
    </div>
  );
}
